import { addDays, isBefore, parseISO } from 'date-fns';

import { FeedbackApiData, FeedbackState, MessageRequestApiData, Visibility } from '../../types/feedback';
import { FeedbackRequest } from './feedback-request';
import { Message } from './message';
import { ReferenceEntity } from './reference';
import { User } from './user';

export interface FeedbackData {
	id: string;
	companyId: string;
	completedAt: string | null;
	createdAt: string;
	expiresAt: string;
	receiver: User;
	receiverId: string;
	reference?: ReferenceEntity;
	referenceId: string | null;
	referenceType: string | null;
	seenByUserIds: string[];
	messages: Message[];
	draftMessages: Message[];
	lastDraft?: Message;
	updatedAt: string;
	template?: { [key: string]: string } | null;

	// New Feedback fields
	giver: User;
	giverId: string;
	llmFeedbackSummary: string | null;
	llmRunId: string | null;
	publishedAt: string | null;
	request: FeedbackRequest | null;
	sharedWithUserIds: string[] | null;
	state: FeedbackState | null;
}

export class Feedback {
	id: string;
	companyId: string;
	completedAt: Date | null;
	createdAt: Date;
	expiresAt: Date;
	receiver: User;
	receiverId: string;
	reference?: ReferenceEntity;
	referenceId: string | null;
	referenceType: string | null;
	seenByUserIds: string[];
	messages: Message[];
	lastDraft?: Message;
	updatedAt: Date;
	template?: { [key: string]: string } | null;

	// New Feedback fields
	giver: User;
	giverId: string;
	llmFeedbackSummary: string | null;
	llmRunId: string | null;
	publishedAt: Date | null;
	request: FeedbackRequest | null;
	sharedWithUserIds: string[] | null;
	state: FeedbackState | null;

	constructor(data: FeedbackData) {
		this.id = data.id;
		this.companyId = data.companyId;
		this.createdAt = parseISO(data.createdAt);
		this.completedAt = data.completedAt ? parseISO(data.completedAt) : null;
		this.expiresAt = data.expiresAt ? parseISO(data.expiresAt) : addDays(this.createdAt, 11);
		this.receiver = data.receiver;
		this.receiverId = data.receiverId;
		this.reference = data.reference;
		this.referenceId = data.referenceId;
		this.referenceType = data.referenceType;
		this.seenByUserIds = data.seenByUserIds;
		this.messages = data.messages;
		this.lastDraft = data.lastDraft;
		this.updatedAt = parseISO(data.updatedAt);

		this.giver = data.giver;
		this.giverId = data.giverId;
		this.llmFeedbackSummary = data.llmFeedbackSummary ?? null;
		this.llmRunId = data.llmRunId ?? null;
		this.publishedAt = data.publishedAt ? parseISO(data.publishedAt) : null;
		this.request = data.request ?? null;
		this.sharedWithUserIds = data.sharedWithUserIds ?? null;
		this.state = data.state ?? null;
		this.template = data.template ?? null;

		if (this.request && this.request.expiresAt) {
			this.expiresAt = this.request.expiresAt;
		}

		if (data.publishedAt) {
			this.completedAt = this.publishedAt;
		}
	}

	giverIs(userId: string) {
		return this.giverId === userId;
	}

	receiverIs(userId: string) {
		return this.receiverId === userId;
	}

	isExpiringSoon() {
		const oneDayFromNow = addDays(Date.now(), 1);

		return isBefore(this.expiresAt, oneDayFromNow) && !this.isExpired();
	}

	isExpired() {
		return isBefore(this.expiresAt, Date.now()) && !this.isComplete();
	}

	isPending() {
		return this.request != null && this.completedAt == null && this.state != FeedbackState.Draft && this.state != FeedbackState.DraftRequest;
	}

	isComplete() {
		return !!this.completedAt;
	}

	isOwnedBy(userId: string) {
		return this.receiverIs(userId) || this.giverIs(userId);
	}

	lastMessage(): Message | undefined {
		return this.messages[this.messages.length - 1];
	}

	firstGiver(): User {
		return this.giver;
	}

	seenBy(userId: string) {
		return this.seenByUserIds.includes(userId);
	}

	lastResponse() {
		return this.messages.reverse().find((m) => m.userId !== this.receiverId);
	}

	sharedTo(userId: string): boolean {
		return this.sharedWithUserIds ? this.sharedWithUserIds.includes(userId) : false;
	}

	isDraft() {
		return this.state === FeedbackState.Draft || this.state === FeedbackState.DraftRequest;
	}

	public static get defaultVisibility() {
		return Visibility.Private;
	}

	public static fromApiData(evaluation: FeedbackApiData) {
		const messageFactory = (message: MessageRequestApiData) => {
			const user = new User({
				id: message.user_id,
				name: message.user.display_name,
				imageUrl: message.user.profile_pic,
				username: message.user.username,
			});

			return new Message({
				createdAt: message.created_at,
				feedbackId: message.feedback_id,
				id: message.id,
				text: message.text,
				updatedAt: message.updated_at,
				user,
				userId: message.user_id,
			});
		};
		const messages = evaluation.messages.map(messageFactory);
		const draftMessages = evaluation.draft_messages?.map(messageFactory);

		const draft = evaluation.draft_messages?.at(-1);
		const lastDraft = draft ? messageFactory(draft) : undefined;

		const receiver = new User({
			id: evaluation.receiver_id,
			name: evaluation.receiver.display_name,
			imageUrl: evaluation.receiver.profile_pic,
			username: evaluation.receiver.username,
		});

		// For new feedback API
		const giver = new User({
			id: evaluation.giver_id,
			name: evaluation.giver.display_name,
			imageUrl: evaluation.giver.profile_pic,
			username: evaluation.giver.username,
		});

		const request = evaluation.request
			? new FeedbackRequest({
					expiresAt: evaluation.request.expires_at,
					id: evaluation.request.id,
					requestedAt: evaluation.request.requested_at,
					text: evaluation.request.text,
				})
			: null;

		return new Feedback({
			id: evaluation.id,
			companyId: evaluation.company_id,
			completedAt: evaluation.completed_at,
			createdAt: evaluation.created_at,
			expiresAt: evaluation.expires_at,
			receiver,
			receiverId: evaluation.receiver_id,
			referenceId: evaluation.reference_id,
			referenceType: evaluation.reference_type,
			seenByUserIds: evaluation.seen_by_user_ids,
			messages,
			lastDraft,
			draftMessages,
			updatedAt: evaluation.updated_at,
			// New Feedback fields
			giver: giver,
			giverId: evaluation.giver_id,
			llmFeedbackSummary: evaluation.llm_feedback_summary,
			llmRunId: evaluation.llm_run_id,
			publishedAt: evaluation.published_at,
			request: request,
			sharedWithUserIds: evaluation.shared_with_user_ids,
			state: evaluation.state,
			template: evaluation.template,
		});
	}
}

export enum EvaluationType {
	Requested = 'requested',
	Unrequested = 'unrequested',
}
